import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

export interface RowMenuItem {
  label: string;
  /** A line under the label saying what it does, where that is not obvious. */
  hint?: string;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * The actions a row does not need on display: behind a "⋯".
 *
 * Suspending or deleting a centre is something done rarely and on purpose, and
 * a red Delete button repeated down every row made the most dangerous thing on
 * the page the most visible. Behind a menu it is one deliberate click further
 * away, which is where it belongs, and the row shows the thing done every day.
 *
 * The menu is drawn at the top of the console rather than inside the row. A
 * table scrolls sideways inside its own card, and anything drawn inside it is
 * cut off at the card's edge — the menu would open and lose its last item.
 */
export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  // Placed against the trigger, below it where there is room and above it
  // where there is not, so a menu on the last row does not open off-screen.
  function place() {
    if (!trigger.current) return;
    const box = trigger.current.getBoundingClientRect();
    const height = menu.current?.offsetHeight ?? 0;
    const below = box.bottom + 6;
    const top = below + height > window.innerHeight - 8 ? Math.max(8, box.top - 6 - height) : below;
    setAt({ top, right: window.innerWidth - box.right });
  }

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    buttons.current.find((b) => b && !b.disabled)?.focus();

    const close = () => setOpen(false);
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        trigger.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    /*
     * The page moving takes the menu with it. Closing on every scroll meant a
     * menu opened low on a zoomed-in screen shut the moment the page moved to
     * show its last item, so that item could never be reached. It follows its
     * button instead, and closes only once the button has left the screen.
     */
    const follow = () => {
      const box = trigger.current?.getBoundingClientRect();
      if (!box || box.bottom < 0 || box.top > window.innerHeight) close();
      else place();
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  function move(delta: number) {
    const enabled = buttons.current.filter((b): b is HTMLButtonElement => Boolean(b && !b.disabled));
    const index = enabled.indexOf(document.activeElement as HTMLButtonElement);
    enabled[(index + delta + enabled.length) % enabled.length]?.focus();
  }

  // Inside the console, so the console's colours and type reach it.
  const host = (typeof document !== 'undefined' && document.querySelector('.shell')) || document.body;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="btn btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="dots" label={label} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            className="menu rowmenu"
            role="menu"
            aria-label={label}
            style={{ top: at?.top ?? -9999, right: at?.right ?? 0 }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                move(1);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                move(-1);
              }
            }}
          >
            {items.map((item, index) => (
              <button
                key={item.label}
                ref={(el) => {
                  buttons.current[index] = el;
                }}
                type="button"
                role="menuitem"
                className={`rowmenu__item${item.danger ? ' rowmenu__item--danger' : ''}`}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.icon && <Icon name={item.icon} size={16} className="rowmenu__icon" />}
                <span className="rowmenu__text">
                  <span className="rowmenu__label">{item.label}</span>
                  {item.hint && <span className="rowmenu__hint">{item.hint}</span>}
                </span>
              </button>
            ))}
          </div>,
          host,
        )}
    </>
  );
}
